import { Check, Close } from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	Autocomplete,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid2,
	IconButton,
	InputAdornment,
	Link,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../theme";
import { getDocumentTypeIcon } from "../../utils/iconUtils";

export interface DocumentItem {
	id?: number;
	name: string;
	urlPath: string;
	content?: string;
	type?: { id: number; label: string } | null;
	uploadedByUserId?: number;
	uploadedBy?: string;
	[key: string]: any;
}

export interface DocumentTableProps {
	documents: DocumentItem[];
	isVisible?: boolean;
	isAddingDocument?: boolean;
	addingDocument?: DocumentItem | null;
	onAddNewDocument?: () => void;
	onFileUpload?: (file: File) => Promise<void>;
	onConfirmAddDocument?: () => void;
	onCancelAddDocument?: () => void;
	onRemoveDocument?: (index: number) => void;
	onDocumentChange?: (document: Partial<DocumentItem>) => void;
	// Optional props for type selection
	showTypeColumn?: boolean;
	documentTypes?: Array<{ id: number; label: string }>;

	getDocumentUrl?: (document: DocumentItem) => string;
	fullWidth?: boolean;

	// File upload configuration
	showFileUpload?: boolean;
	fileAccept?: string;
	// Display mode
	displayMode?: boolean;
	// Header visibility
	showHeader?: boolean;
	// Styling
	tableWidth?: string;
	documentColumnWidth?: string;
	typeColumnWidth?: string;
	actionColumnWidth?: string;
	// Error handling
	hasFieldError?: (fieldPath: string) => boolean;
	getFieldError?: (fieldPath: string) => string | undefined;
	errorFieldPath?: string;
	// Default URL generation for freelancer context
	defaultContainerName?: string;
	experiencesContainerName?: string;
	baseUrl?: string;
	useExperiencesContainer?: boolean;
	// Current user ID for permission checks (e.g., only allow deleting own documents)
	currentUserId?: number;
}

const DocumentTable: React.FC<DocumentTableProps> = ({
	documents,
	isVisible = true,
	isAddingDocument = false,
	addingDocument = null,
	onAddNewDocument = () => {},
	onFileUpload,
	onConfirmAddDocument = () => {},
	onCancelAddDocument = () => {},
	onRemoveDocument,
	onDocumentChange,
	showTypeColumn = false,
	documentTypes = [],
	getDocumentUrl: getDocumentUrlProp,
	fullWidth = false,
	showFileUpload = true,
	fileAccept = ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png",
	displayMode = false,
	showHeader = true,
	tableWidth = "100%",
	documentColumnWidth = "660px",
	typeColumnWidth = "500px",
	actionColumnWidth = "300px",
	hasFieldError,
	getFieldError,
	errorFieldPath,
	defaultContainerName,
	experiencesContainerName,
	baseUrl,
	useExperiencesContainer = false,
	currentUserId
}) => {
	const { t } = useTranslation();
	const [isDragging, setIsDragging] = useState(false);
	const [documentToDelete, setDocumentToDelete] = useState<{
		index: number;
		name: string;
	} | null>(null);

	// Internal labels - not customizable
	const effectiveShowTypeColumn = displayMode ? true : showTypeColumn;
	const documentColumnLabel = t("documentTable.columns.document");
	const typeColumnLabel = t("documentTable.columns.type");
	const addDocumentLabel = t("documentTable.actions.addDocument");

	if (!isVisible) return null;

	const getDocumentUrl = (document: DocumentItem): string => {
		// Use custom URL generator if provided
		if (getDocumentUrlProp) {
			return getDocumentUrlProp(document);
		}

		// Default behavior for freelancer documents
		if (document.content) {
			return document.urlPath;
		}

		if (document.urlPath.startsWith("http")) {
			return document.urlPath;
		}

		// Use provided base URL or fall back to env variable
		const storageBaseUrl = baseUrl || process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL;

		const containerName = useExperiencesContainer
			? experiencesContainerName ||
			  process.env.REACT_APP_FREELANCER_PROFILE_EXPERIENCES_DOCUMENTS_CONTAINER_NAME
			: defaultContainerName ||
			  process.env.REACT_APP_FREELANCER_PROFILE_DOCUMENTS_CONTAINER_NAME;

		return `${storageBaseUrl}/${containerName}/${document.urlPath}`;
	};

	const handleFileInputClick = () => {
		if (!onFileUpload) return;

		const input = document.createElement("input");
		input.type = "file";
		input.accept = fileAccept;
		input.onchange = async e => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (file) {
				await onFileUpload(file);
			}
		};
		input.click();
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const handleDrop = async (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);

		if (!onFileUpload) return;

		const files = e.dataTransfer.files;
		if (files && files.length > 0) {
			await onFileUpload(files[0]);
		}
	};

	const handleDeleteClick = (index: number, documentName: string) => {
		setDocumentToDelete({ index, name: documentName });
	};

	const handleConfirmDelete = () => {
		if (documentToDelete !== null) {
			onRemoveDocument?.(documentToDelete.index);
			setDocumentToDelete(null);
		}
	};

	const handleCancelDelete = () => {
		setDocumentToDelete(null);
	};

	const tableContent = (
		<>
			<TableContainer component={Paper} elevation={1} sx={{ mt: 2, width: tableWidth }}>
				<Table>
					{showHeader && (
						<TableHead>
							<TableRow>
								{effectiveShowTypeColumn && (
									<TableCell sx={{ width: typeColumnWidth, fontWeight: 600 }}>
										{typeColumnLabel}
									</TableCell>
								)}
								<TableCell
									sx={{
										width: effectiveShowTypeColumn ? "560px" : documentColumnWidth,
										fontWeight: 600
									}}
								>
									{documentColumnLabel}
								</TableCell>
								{!displayMode && (
									<TableCell
										align="center"
										sx={{ width: actionColumnWidth, fontWeight: 600 }}
									>
										<Link
											href="#"
											onClick={e => {
												e.preventDefault();
												onAddNewDocument();
											}}
										>
											{addDocumentLabel}
										</Link>
									</TableCell>
								)}
							</TableRow>
						</TableHead>
					)}
					<TableBody>
						{/* Show existing documents */}
						{documents.map((document, docIndex) => (
							<TableRow key={`doc-${document.name}-${docIndex}`}>
								{effectiveShowTypeColumn && (
									<TableCell>
										<Typography variant="body1" color="text.secondary">
											<Box
												component="li"
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1
												}}
											>
												{document.type && getDocumentTypeIcon(document.type.label)}
												{document.type && t(`DocumentTypes.${document.type.label}`)}
											</Box>
										</Typography>
									</TableCell>
								)}
								<TableCell>
									<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
										<Link
											variant="body1"
											color="secondary"
											sx={{
												flex: 1,
												textDecoration: "none",
												fontWeight: "medium",
												cursor: "pointer",
												border: "none",
												background: "none",
												padding: 0,
												"&:hover": {
													textDecoration: "underline"
												}
											}}
											href={getDocumentUrl(document)}
											target="_blank"
										>
											{document.name}
										</Link>
									</Box>
								</TableCell>
								{!displayMode && (
									<TableCell align="center">
										{/* Show delete button only if:
										    - onRemoveDocument is provided AND
										    - (no currentUserId OR document uploaded by current user) */}
										{onRemoveDocument &&
											(!currentUserId || document.uploadedByUserId === currentUserId) && (
												<IconButton
													size="small"
													onClick={() => handleDeleteClick(docIndex, document.name)}
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											)}
									</TableCell>
								)}
							</TableRow>
						))}

						{/* Add new document row */}
						{!displayMode && (isAddingDocument || documents.length === 0) && (
							<TableRow>
								{showTypeColumn && (
									<TableCell sx={{ paddingBottom: "40px" }}>
										<Autocomplete
											size="small"
											options={documentTypes}
											getOptionLabel={option => t(`DocumentTypes.${option.label}`)}
											value={
												documentTypes.find(x => x.id === addingDocument?.type?.id) || null
											}
											onChange={(_, value) => {
												if (onDocumentChange) {
													onDocumentChange({ type: value });
												}
											}}
											renderOption={(props, option) => (
												<Box
													component="li"
													{...props}
													sx={{ display: "flex", alignItems: "center", gap: 1 }}
												>
													{getDocumentTypeIcon?.(option.label)}
													{t(`DocumentTypes.${option.label}`)}
												</Box>
											)}
											renderInput={params => (
												<TextField
													{...params}
													placeholder={t("documentTable.selectType")}
													variant="outlined"
													slotProps={{
														input: {
															...params.InputProps,
															startAdornment: addingDocument?.type ? (
																<InputAdornment position="start">
																	{getDocumentTypeIcon?.(addingDocument.type.label)}
																</InputAdornment>
															) : null
														}
													}}
												/>
											)}
										/>
									</TableCell>
								)}
								<TableCell sx={{ paddingBottom: "40px" }}>
									{showFileUpload ? (
										<Box
											sx={{
												border: isDragging
													? `2px dashed ${zoullooColors.secondary.main}`
													: "2px dashed #c4c4c4",
												borderRadius: "8px",
												padding: "20px",
												textAlign: "center",
												backgroundColor: isDragging ? "#f0f7ff" : "#fafafa",
												cursor: "pointer",
												transition: "all 0.3s ease",
												"&:hover": {
													borderColor: zoullooColors.secondary.main,
													backgroundColor: "#f5f5f5"
												}
											}}
											onClick={handleFileInputClick}
											onDragOver={handleDragOver}
											onDragLeave={handleDragLeave}
											onDrop={handleDrop}
										>
											<CloudUploadIcon
												sx={{
													fontSize: 48,
													color: zoullooColors.grey[400],
													mb: 1
												}}
											/>
											<Typography
												variant="body1"
												sx={{ color: zoullooColors.grey[600], mb: 1 }}
											>
												{addingDocument?.name || t("documentTable.uploadPlaceholder")}
											</Typography>
											<Typography variant="body2" sx={{ color: zoullooColors.grey[500] }}>
												{t("documentTable.supportedFormats")}
											</Typography>
										</Box>
									) : (
										<TextField
											fullWidth
											placeholder={t("documentTable.uploadPlaceholder")}
											value={addingDocument?.name || ""}
											onChange={e => {
												if (onDocumentChange) {
													onDocumentChange({
														name: e.target.value,
														urlPath: e.target.value
													});
												}
											}}
											variant="outlined"
											size="small"
										/>
									)}
								</TableCell>
								<TableCell align="center" sx={{ paddingBottom: "40px" }}>
									<IconButton sx={{ mr: 1 }} onClick={onCancelAddDocument} size="small">
										<Close />
									</IconButton>
									<IconButton
										disabled={
											!addingDocument?.name || (showTypeColumn && !addingDocument?.type)
										}
										onClick={onConfirmAddDocument}
										size="small"
									>
										<Check />
									</IconButton>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Error display */}
			{hasFieldError &&
				getFieldError &&
				errorFieldPath &&
				hasFieldError(errorFieldPath) && (
					<Typography color="error" variant="body2" sx={{ mt: 1, display: "block" }}>
						{getFieldError(errorFieldPath)}
					</Typography>
				)}
		</>
	);

	// Confirmation Dialog for delete
	const confirmationDialog = (
		<Dialog
			open={documentToDelete !== null}
			onClose={handleCancelDelete}
			aria-labelledby="delete-document-dialog-title"
			aria-describedby="delete-document-dialog-description"
		>
			<DialogTitle id="delete-document-dialog-title">
				{t("documentTable.deleteDialog.title")}
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="delete-document-dialog-description">
					{t("documentTable.deleteDialog.message", {
						documentName: documentToDelete?.name
					})}
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancelDelete} color="inherit">
					{t("common.cancel")}
				</Button>
				<Button
					onClick={handleConfirmDelete}
					color="secondary"
					variant="contained"
					autoFocus
				>
					{t("documentTable.deleteDialog.confirm")}
				</Button>
			</DialogActions>
		</Dialog>
	);

	// If fullWidth is true, return content without Grid wrapper
	if (fullWidth) {
		return (
			<>
				{tableContent}
				{confirmationDialog}
			</>
		);
	}

	// Otherwise, wrap in Grid2 for freelancer onboarding layout
	return (
		<Grid2 size={{ xs: 12, md: 10 }}>
			{tableContent}
			{confirmationDialog}
		</Grid2>
	);
};

export default DocumentTable;
